import {
  FlatCustomFieldMaps,
  ReducerState
} from '@components/asset/modal/tabs/edit/EditTabTypes';
import { isDirty } from '@components/asset/modal/tabs/edit/helpers';
import { CreateCustomFieldParams } from '@/api/v4/assets/customFieldTypes';

export interface CustomFieldChanges {
  updatedCustomFieldMaps: FlatCustomFieldMaps[];
  deletedCustomFieldIds: string[];
  createdCustomFieldMaps: CreateCustomFieldParams[];
}

enum CustomFieldsChangeValues {
  ExistingCustomFields,
  CreateCustomFields
}

const getCustomFieldUpdates = (changes: Partial<CustomFieldChanges>, state: ReducerState): Partial<CustomFieldChanges> => {
  const editCustomFieldMaps = state.editState.customFieldMaps;
  const initialCustomFieldMaps = state.initialData.customFieldMaps;
  if (editCustomFieldMaps) {
    // custom field edit state is an array, check each custom field for isDirty
    const customFieldChangeMap = editCustomFieldMaps.filter((field: FlatCustomFieldMaps, index: number) => {
      if (field.value.id === null) return true;
      const matchingInitialValue = initialCustomFieldMaps.find((initialMap) => initialMap.value.id === field.value.id);
      return isDirty<string>(matchingInitialValue.value.value, field.value.value);
    });

    const updatedCustomFieldMaps = customFieldChangeMap.filter(({ value }) => value.value !== '');
    // only delete existing field maps, not new values
    const deletedCustomFieldMaps = customFieldChangeMap.filter(({ value }) => value.id && value.value === '');
    let customFieldChanges: Partial<CustomFieldChanges> = {};
    if (customFieldChangeMap.length > 0) {
      if (updatedCustomFieldMaps.length > 0) {
        customFieldChanges = { updatedCustomFieldMaps };
      }
      if (deletedCustomFieldMaps.length > 0) {
        const deletedCustomFieldIds = deletedCustomFieldMaps.map((field) => field.value.id);
        customFieldChanges = {
          ...customFieldChanges,
          deletedCustomFieldIds
        };
      }

      return {
        ...changes,
        ...customFieldChanges
      };
    }
  }
  return changes;
};

const getNewCustomFields = (changes: Partial<CustomFieldChanges>, state: ReducerState): Partial<CustomFieldChanges> => {
  // clean up any empty fields
  const sanitizedCreatedCustomFields = state.editState.createCustomFieldMaps.filter((field: CreateCustomFieldParams) => (
    (field.key !== '') && (field.value !== '')
  ));
  if (sanitizedCreatedCustomFields.length !== 0) {
    return { ...changes, createdCustomFieldMaps: sanitizedCreatedCustomFields };
  }
  return changes;
};

const customFieldsChangeMap = {
  [CustomFieldsChangeValues.ExistingCustomFields]: getCustomFieldUpdates,
  [CustomFieldsChangeValues.CreateCustomFields]: getNewCustomFields
};

const getCustomFieldChanges = (state): Partial<CustomFieldChanges> | false => {
  let changes: Partial<CustomFieldChanges> = {};
  changes = Object.keys(customFieldsChangeMap).reduce((prevChanges, key) => customFieldsChangeMap[key](prevChanges, state), changes);
  return Object.keys(changes).length > 0 && changes;
};

export default getCustomFieldChanges;
